import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { pricingService } from '../services/api';
import type { PricingPlan, PricingTier, PricingPack, SeasonalRate } from '../types';

// State interfaces
interface PricingState {
  plans: PricingPlan[];
  tiers: PricingTier[];
  packs: PricingPack[];
  rates: SeasonalRate[];
  loading: boolean;
  error: string | null;
}

const initialState: PricingState = {
  plans: [],
  tiers: [],
  packs: [],
  rates: [],
  loading: false,
  error: null,
};

// Async thunks for Plans
export const fetchPlans = createAsyncThunk(
  'pricing/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const response = await pricingService.getPlans();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch plans');
    }
  }
);

export const createPlan = createAsyncThunk(
  'pricing/createPlan',
  async (data: Partial<PricingPlan>, { rejectWithValue }) => {
    try {
      const response = await pricingService.createPlan(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create plan');
    }
  }
);

export const updatePlan = createAsyncThunk(
  'pricing/updatePlan',
  async ({ id, data }: { id: number; data: Partial<PricingPlan> }, { rejectWithValue }) => {
    try {
      const response = await pricingService.updatePlan(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update plan');
    }
  }
);

export const deletePlan = createAsyncThunk(
  'pricing/deletePlan',
  async (id: number, { rejectWithValue }) => {
    try {
      await pricingService.deletePlan(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete plan');
    }
  }
);

export const activatePlan = createAsyncThunk(
  'pricing/activatePlan',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await pricingService.activatePlan(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to activate plan');
    }
  }
);

export const deactivatePlan = createAsyncThunk(
  'pricing/deactivatePlan',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await pricingService.deactivatePlan(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to deactivate plan');
    }
  }
);

export const duplicatePlan = createAsyncThunk(
  'pricing/duplicatePlan',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await pricingService.duplicatePlan(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to duplicate plan');
    }
  }
);

// Async thunks for Tiers
export const fetchTiers = createAsyncThunk(
  'pricing/fetchTiers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await pricingService.getTiers();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tiers');
    }
  }
);

export const createTier = createAsyncThunk(
  'pricing/createTier',
  async (data: Partial<PricingTier>, { rejectWithValue }) => {
    try {
      const response = await pricingService.createTier(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create tier');
    }
  }
);

export const updateTier = createAsyncThunk(
  'pricing/updateTier',
  async ({ id, data }: { id: number; data: Partial<PricingTier> }, { rejectWithValue }) => {
    try {
      const response = await pricingService.updateTier(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tier');
    }
  }
);

export const deleteTier = createAsyncThunk(
  'pricing/deleteTier',
  async (id: number, { rejectWithValue }) => {
    try {
      await pricingService.deleteTier(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete tier');
    }
  }
);

export const reorderTiers = createAsyncThunk(
  'pricing/reorderTiers',
  async (tiers: Array<{ id: number; sort_order: number }>, { rejectWithValue }) => {
    try {
      await pricingService.reorderTiers(tiers);
      return tiers;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reorder tiers');
    }
  }
);

// Async thunks for Packs
export const fetchPacks = createAsyncThunk(
  'pricing/fetchPacks',
  async (params: { plan_id?: number; category_id?: number } | undefined = undefined, { rejectWithValue }) => {
    try {
      const response = await pricingService.getPacks(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch packs');
    }
  }
);

export const createPack = createAsyncThunk(
  'pricing/createPack',
  async (data: Partial<PricingPack>, { rejectWithValue }) => {
    try {
      const response = await pricingService.createPack(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create pack');
    }
  }
);

export const updatePack = createAsyncThunk(
  'pricing/updatePack',
  async ({ id, data }: { id: number; data: Partial<PricingPack> }, { rejectWithValue }) => {
    try {
      const response = await pricingService.updatePack(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update pack');
    }
  }
);

export const deletePack = createAsyncThunk(
  'pricing/deletePack',
  async (id: number, { rejectWithValue }) => {
    try {
      await pricingService.deletePack(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete pack');
    }
  }
);

// Async thunks for Rates
export const fetchRates = createAsyncThunk(
  'pricing/fetchRates',
  async (params: { pack_id?: number; tier_id?: number; start_date?: string; end_date?: string } | undefined = undefined, { rejectWithValue }) => {
    try {
      const response = await pricingService.getRates(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rates');
    }
  }
);

export const createRate = createAsyncThunk(
  'pricing/createRate',
  async (data: Partial<SeasonalRate>, { rejectWithValue }) => {
    try {
      const response = await pricingService.createRate(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create rate');
    }
  }
);

export const updateRate = createAsyncThunk(
  'pricing/updateRate',
  async ({ id, data }: { id: number; data: Partial<SeasonalRate> }, { rejectWithValue }) => {
    try {
      const response = await pricingService.updateRate(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update rate');
    }
  }
);

export const deleteRate = createAsyncThunk(
  'pricing/deleteRate',
  async (id: number, { rejectWithValue }) => {
    try {
      await pricingService.deleteRate(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete rate');
    }
  }
);

export const bulkCreateRates = createAsyncThunk(
  'pricing/bulkCreateRates',
  async (data: { pricing_pack_id: number; start_date: string; end_date: string; rates: Array<{ pricing_tier_id: number; price_ht: number; color?: string }> }, { rejectWithValue }) => {
    try {
      const response = await pricingService.bulkCreateRates(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk create rates');
    }
  }
);

// Slice
const pricingSlice = createSlice({
  name: 'pricing',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Plans
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.plans.push(action.payload);
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        const index = state.plans.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.plans[index] = action.payload;
        }
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.plans = state.plans.filter(p => p.id !== action.payload);
      })
      .addCase(activatePlan.fulfilled, (state, action) => {
        const index = state.plans.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.plans[index] = action.payload;
        }
      })
      .addCase(deactivatePlan.fulfilled, (state, action) => {
        const index = state.plans.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.plans[index] = action.payload;
        }
      })
      .addCase(duplicatePlan.fulfilled, (state, action) => {
        state.plans.push(action.payload);
      });

    // Tiers
    builder
      .addCase(fetchTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTiers.fulfilled, (state, action) => {
        state.loading = false;
        state.tiers = action.payload;
      })
      .addCase(fetchTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTier.fulfilled, (state, action) => {
        state.tiers.push(action.payload);
      })
      .addCase(updateTier.fulfilled, (state, action) => {
        const index = state.tiers.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tiers[index] = action.payload;
        }
      })
      .addCase(deleteTier.fulfilled, (state, action) => {
        state.tiers = state.tiers.filter(t => t.id !== action.payload);
      })
      .addCase(reorderTiers.fulfilled, (state, action) => {
        action.payload.forEach(({ id, sort_order }) => {
          const tier = state.tiers.find(t => t.id === id);
          if (tier) {
            tier.sort_order = sort_order;
          }
        });
        state.tiers.sort((a, b) => a.sort_order - b.sort_order);
      });

    // Packs
    builder
      .addCase(fetchPacks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPacks.fulfilled, (state, action) => {
        state.loading = false;
        state.packs = action.payload;
      })
      .addCase(fetchPacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createPack.fulfilled, (state, action) => {
        state.packs.push(action.payload);
      })
      .addCase(updatePack.fulfilled, (state, action) => {
        const index = state.packs.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.packs[index] = action.payload;
        }
      })
      .addCase(deletePack.fulfilled, (state, action) => {
        state.packs = state.packs.filter(p => p.id !== action.payload);
      });

    // Rates
    builder
      .addCase(fetchRates.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Avoid showing the previous pack's seasonal rates while the new pack loads
        state.rates = [];
      })
      .addCase(fetchRates.fulfilled, (state, action) => {
        state.loading = false;
        state.rates = action.payload;
      })
      .addCase(fetchRates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createRate.fulfilled, (state, action) => {
        state.rates.push(action.payload);
      })
      .addCase(updateRate.fulfilled, (state, action) => {
        const index = state.rates.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.rates[index] = action.payload;
        }
      })
      .addCase(deleteRate.fulfilled, (state, action) => {
        state.rates = state.rates.filter(r => r.id !== action.payload);
      })
      .addCase(bulkCreateRates.fulfilled, (state, action) => {
        state.rates.push(...action.payload);
      });
  },
});

export const { clearError } = pricingSlice.actions;
export default pricingSlice.reducer;

